import { type EarningsCsvLabels, type LedgerRange, type LedgerStatus } from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';
import { FilterPills, type FilterOption } from './filter-pills.js';
import { ExportCsvButton } from './export-csv-button.js';

/**
 * Range + status filter pills and the "Export CSV" button — the control
 * strip shared by `/earnings` (agent) and `/recruiter/earnings`. Persona-
 * neutral; each caller passes its own filtered-set exporter. All labels
 * resolve through `useI18n()` so there are no literals here.
 */
export function EarningsControls({
  range,
  status,
  onRange,
  onStatus,
  onExport,
  exportDisabled,
}: {
  readonly range: LedgerRange;
  readonly status: LedgerStatus;
  readonly onRange: (next: LedgerRange) => void;
  readonly onStatus: (next: LedgerStatus) => void;
  readonly onExport: () => void;
  readonly exportDisabled: boolean;
}) {
  const t = useI18n();
  const rangeOptions: FilterOption<LedgerRange>[] = [
    { value: 'this_month', label: t.t('agent.range.thisMonth') },
    { value: 'last_month', label: t.t('agent.range.lastMonth') },
    { value: 'all_time', label: t.t('agent.range.allTime') },
  ];
  const statusOptions: FilterOption<LedgerStatus>[] = [
    { value: 'all', label: t.t('agent.earnings.status.all') },
    { value: 'unpaid', label: t.t('agent.earnings.status.unpaid') },
    { value: 'paid', label: t.t('agent.earnings.status.paidOut') },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <FilterPills
          ariaLabel={t.t('agent.range.ariaLabel')}
          value={range}
          options={rangeOptions}
          onChange={onRange}
        />
        <ExportCsvButton
          label={t.t('agent.earnings.exportCsv')}
          onClick={onExport}
          disabled={exportDisabled}
        />
      </div>
      <FilterPills
        ariaLabel={t.t('agent.earnings.statusFilterAria')}
        value={status}
        options={statusOptions}
        onChange={onStatus}
      />
    </div>
  );
}

/** Resolve the localised earnings-CSV column headers from the i18n instance. */
export function earningsCsvLabels(i18n: { t: (key: string) => string }): EarningsCsvLabels {
  return {
    date: i18n.t('agent.earnings.csv.date'),
    kind: i18n.t('agent.earnings.csv.kind'),
    operator: i18n.t('agent.earnings.csv.operator'),
    invoice: i18n.t('agent.earnings.csv.invoice'),
    rate: i18n.t('agent.earnings.csv.rate'),
    multiplier: i18n.t('agent.earnings.csv.multiplier'),
    amount: i18n.t('agent.earnings.csv.amount'),
    payout: i18n.t('agent.earnings.csv.payout'),
  };
}

/** `YYYYMMDD` stamp for CSV filenames, from an epoch-ms instant. */
export function csvDateStamp(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}
